import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { PlanApi } from "./api";
import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast, useToast } from "@shared/hooks/use-toast";

export const useBillingFrequency = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.billingFrequency(),
    () => PlanApi.getBillingFrequency(),
  );
};
export const useIncludedIn = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.includedIn(),
    () => PlanApi.getIncludedIn(),
  );
};

export const useCategoryOptions = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.category();
  return useSmartQuery(key, () => PlanApi.getCategories(remoteUtilityId));
};

export const useSubcategoryOptions = (categoryCode: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.subCategory(categoryCode);
  return useSmartQuery(
    key,
    () => PlanApi.getSubcategories(remoteUtilityId, categoryCode),
    {
      enabled: !!remoteUtilityId && !!categoryCode,
    }
  );
};

export const useContract = (categories?: any, subCategories?: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.filteredContractList(
      categories,
      subCategories
    ),
    () =>
      PlanApi.getContractsData(
        remoteUtilityId,
        categories,
        subCategories
      ),
    {
      enabled,
    }
  );
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();
  return useSmartMutation(PlanApi.createPlan, {
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["onboarding", "planList", "list"] });
    },
    onError: (error: Error) => {
    },
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();
  return useSmartMutation(
    ({ planId, payload }: { planId: string | number; payload: any }) =>
      PlanApi.updatePlan(planId, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: ["onboarding", "planList", "list"] });
      },
      onError: (error: Error) => {},
    }
  );
};

export const useUtilityServices = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.utilityServices(remoteUtilityId);
  return useSmartQuery(key, () => PlanApi.getUtilityServices(remoteUtilityId), {
    enabled: !!remoteUtilityId,
  });
};

export const useGetRate = (utilityService: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.rate(remoteUtilityId, utilityService);
  return useSmartQuery(key, () => PlanApi.getRate(remoteUtilityId, utilityService), {
    enabled: !!remoteUtilityId && !!utilityService,
  });
};

export const useGetParameter = (utilityService?: string | string[]) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.parameter(remoteUtilityId, utilityService);
  return useSmartQuery(key, () => PlanApi.getParameter(remoteUtilityId, utilityService), {
    enabled: !!remoteUtilityId,
    staleTime: Infinity,
  });
};

export const useGetPlanDetail = (planId?: string | number) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.detail(planId as string | number);
  return useSmartQuery(key, () => PlanApi.getPlanDetail(planId!, remoteUtilityId), {
    enabled: !!remoteUtilityId && !!planId,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });
};
export const useGetPlanDetailByTab = (planId: string, tab: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.detailByTab(planId as string | number, tab as string);
  return useSmartQuery(key, () => PlanApi.getPlanDetailByTab(planId, remoteUtilityId, tab), {
    enabled: !!remoteUtilityId && !!planId && !!tab,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  }); 
};
export const useClonePlan = () =>
  useSmartMutation(
    (planID: string) =>
      PlanApi.clonePlan(planID),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Plan cloned successfully",
        });      },
      onError: (error: Error) => {
        toast({
          title: "Error!",
          description: "Failed to clone plan",
        });
      },
    }
  );

  export const useGetParameterValues = (parameterId: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.plan.parameterValues(remoteUtilityId, parameterId);
  return useSmartQuery(key, () => PlanApi.getParameterValues(remoteUtilityId, parameterId), {
    enabled: !!remoteUtilityId && !!parameterId,
    staleTime: Infinity,
  });
};
export const usePublishPlan = () => {
  const queryClient = useQueryClient();
  return useSmartMutation(
    (params: { planID: string; payload: any }) =>
      PlanApi.publishPlan(params.planID, params.payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: ["onboarding", "planList", "list"] });
      },
      onError: (error: Error) => {},
    }
  );
};

export const usePlanList = (filters: any, remoteUtilityId?: string) => {
  const queryKey = QueryKeyFactory.module.onboarding.planTarrif.planTable(filters);
  const isEnabled = !!remoteUtilityId;

  return useSmartQuery(queryKey, () => PlanApi.getPlanList({ ...filters, remote_utility_id: remoteUtilityId }), {
    enabled: isEnabled,
  });
};

export const useSchedulePlan = () => {
  const queryClient = useQueryClient();


  return useMutation({
    mutationFn: (params: {planId: string; payload: any }) => 
      PlanApi.schedulePlan(params.planId, params.payload),
  });
};
  export const useGetPlanListForEdit = (filters?: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.listForEdit(remoteUtilityId as string, filters),
    () => PlanApi.getPlanListForEdit(remoteUtilityId as string, filters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const usePreviewPlanEditDetails = () => {
  return useSmartMutation(
    (data: any) => PlanApi.previewPlanEditDetails(data)
  );
};

export const useApplyPlanEditDetails = () => {
  return useSmartMutation(
    (data: any) => PlanApi.applyPlanEditDetails(data)
  );
};

export const usePlanChargesChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.chargesChoices(),
    () => PlanApi.getPlanChargesChoices(),
  );
};

export const useApplyCommonServices = () => {
  return useSmartMutation(
    (data: any) => PlanApi.applyCommonServices(data)
  );
};

export const usePlanVersionList = (planId: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId && !!planId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.versionList(planId),
    () => PlanApi.getPlanVersionList(planId, remoteUtilityId),
    {
      enabled,
      refetchOnMount: "always",
    }
  );
};

export const usePlanVersionDetail = (planId: string, versionId: string) => {
  const remoteUtilityId =useRemoteUtilityId();
  const enabled = !!remoteUtilityId && !!planId && !!versionId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.plan.versionDetail(planId, versionId),
    () => PlanApi.getPlanVersionDetail(planId, remoteUtilityId, versionId),
    {
      enabled,
      refetchOnMount: "always",
      staleTime: 0,
      gcTime: 0,
    }
  );
};

